import { useEffect, useRef } from "react";
import { behaviorLogWriter } from "./behaviorLogWriter.js";

export { flushBehaviorTracking } from "./behaviorLogWriter.js";

const STORAGE_VERSION = 1;
const MAX_AUTHORED_ENTRIES = 150;
const MAX_AUTHORED_TEXT_LENGTH = 4000;
const DUPLICATE_EVENT_WINDOW_MS = 50;

function getStorageKey(playerId) {
  return `integrityTracking:${playerId}`;
}

function selectionCarriesCopyTrap() {
  // The scenario components render the hidden instruction inside a span marked
  // `data-copy-trap`. Detecting it structurally rather than by matching the
  // trap string keeps this helper working when the trap text is reworded.
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }
  try {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return false;
    }
    const traps = document.querySelectorAll("[data-copy-trap]");
    for (let index = 0; index < traps.length; index += 1) {
      if (selection.containsNode(traps[index], true)) {
        return true;
      }
    }
  } catch (_) {
    return false;
  }
  return false;
}

function normalizeText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function clipText(value, maxLength = MAX_AUTHORED_TEXT_LENGTH) {
  return String(value || "").slice(0, maxLength);
}

function getDefaultCache() {
  return {
    version: STORAGE_VERSION,
    lastCopyCut: null,
    authoredEntries: [],
    lastPersistedEvent: null,
  };
}

function loadCache(playerId) {
  if (!playerId || typeof window === "undefined") {
    return getDefaultCache();
  }

  try {
    const raw = window.sessionStorage.getItem(getStorageKey(playerId));
    if (!raw) {
      return getDefaultCache();
    }

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== STORAGE_VERSION) {
      return getDefaultCache();
    }

    return {
      ...getDefaultCache(),
      ...parsed,
      authoredEntries: Array.isArray(parsed.authoredEntries)
        ? parsed.authoredEntries
        : [],
    };
  } catch (_) {
    return getDefaultCache();
  }
}

function saveCache(playerId, cache) {
  if (!playerId || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(getStorageKey(playerId), JSON.stringify(cache));
  } catch (_) {
    // Ignore storage failures; tracking should stay best-effort.
  }
}

function isEditableElement(target) {
  if (!target || typeof target !== "object") {
    return false;
  }

  const tagName = String(target.tagName || "").toLowerCase();
  if (tagName === "textarea") return true;
  if (tagName === "input") {
    const type = String(target.type || "text").toLowerCase();
    return !["checkbox", "radio", "button", "submit", "reset", "file"].includes(type);
  }

  return target.isContentEditable === true;
}

function getEditableValue(target) {
  if (!isEditableElement(target)) {
    return "";
  }

  if (typeof target.value === "string") {
    return target.value;
  }

  return String(target.textContent || "");
}

function getIntegrityFieldId(target) {
  if (!target || typeof target !== "object") {
    return null;
  }

  const explicitField =
    typeof target.getAttribute === "function"
      ? target.getAttribute("data-integrity-field")
      : null;
  if (explicitField) {
    return explicitField;
  }

  if (target.name) {
    return String(target.name);
  }
  if (target.id) {
    return String(target.id);
  }
  if (target.tagName) {
    return String(target.tagName).toLowerCase();
  }

  return null;
}

function getSelectionTextFromEditable(target) {
  if (!isEditableElement(target) || typeof target.value !== "string") {
    return "";
  }

  const start = Number.isInteger(target.selectionStart) ? target.selectionStart : null;
  const end = Number.isInteger(target.selectionEnd) ? target.selectionEnd : null;
  if (start === null || end === null || end <= start) {
    return "";
  }

  return target.value.slice(start, end);
}

function getSelectionText(event) {
  const target = event?.target;
  const editableSelection = getSelectionTextFromEditable(target);
  if (editableSelection) {
    return editableSelection;
  }

  try {
    const browserSelection = window.getSelection?.();
    if (browserSelection) {
      const selected = String(browserSelection.toString() || "");
      if (selected) {
        return selected;
      }
    }
  } catch (_) {
    // Ignore selection lookup failures.
  }

  return "";
}

function getPlainTextFromClipboardEvent(event) {
  return String(
    event?.clipboardData?.getData("text/plain") ||
      event?.nativeEvent?.clipboardData?.getData("text/plain") ||
      ""
  );
}

function shouldCaptureAuthoredInput(inputType) {
  if (!inputType) {
    return false;
  }

  if (
    inputType === "insertFromPaste" ||
    inputType === "insertFromDrop" ||
    inputType === "historyUndo" ||
    inputType === "historyRedo" ||
    inputType === "deleteByCut"
  ) {
    return false;
  }

  return (
    inputType.startsWith("insert") ||
    inputType.startsWith("delete")
  );
}

function rememberAuthoredEntry(cache, entry) {
  const text = clipText(entry?.text);
  const normalized = normalizeText(text);
  if (!normalized) {
    return cache;
  }

  const authoredEntries = Array.isArray(cache.authoredEntries)
    ? [...cache.authoredEntries]
    : [];
  const lastEntry = authoredEntries[authoredEntries.length - 1];

  if (
    lastEntry &&
    lastEntry.normalized === normalized &&
    lastEntry.fieldId === entry.fieldId &&
    lastEntry.sourceType === entry.sourceType
  ) {
    return cache;
  }

  authoredEntries.push({
    text,
    normalized,
    fieldId: entry.fieldId || null,
    sourceType: entry.sourceType || "typed_snapshot",
    sourceAt: Number(entry.sourceAt || Date.now()),
    phase: entry.phase || null,
    stageId: entry.stageId || null,
    stageName: entry.stageName || null,
  });

  return {
    ...cache,
    authoredEntries: authoredEntries.slice(-MAX_AUTHORED_ENTRIES),
  };
}

function findPriorAuthoredMatch(cache, pastedText) {
  const normalizedPaste = normalizeText(pastedText);
  if (!normalizedPaste) {
    return null;
  }

  const authoredEntries = Array.isArray(cache.authoredEntries)
    ? cache.authoredEntries
    : [];

  for (let index = authoredEntries.length - 1; index >= 0; index -= 1) {
    const entry = authoredEntries[index];
    const authoredText = String(entry?.normalized || "");
    if (!authoredText) {
      continue;
    }

    if (authoredText === normalizedPaste || authoredText.includes(normalizedPaste)) {
      return entry;
    }
  }

  return null;
}

function sortEventLog(events) {
  return [...events].sort((left, right) => {
    const leftTime = Number(left?.t || 0);
    const rightTime = Number(right?.t || 0);
    return leftTime - rightTime;
  });
}

function buildDuplicateFingerprint(event) {
  if (event.type === "paste") {
    return [
      event.type,
      event.provenance,
      event.fieldId || "",
      normalizeText(event.pastedText).slice(0, 120),
    ].join("|");
  }

  return [
    event.type,
    event.action || "",
    event.fieldId || "",
    event.stageId || "",
    event.stageName || "",
  ].join("|");
}

function shouldDropDuplicate(cache, event) {
  const fingerprint = buildDuplicateFingerprint(event);
  const detectedAt = Number(event.detectedAt || Date.now());
  const previous = cache.lastPersistedEvent;

  const isDuplicate =
    previous &&
    previous.fingerprint === fingerprint &&
    Math.abs(detectedAt - Number(previous.detectedAt || 0)) <= DUPLICATE_EVENT_WINDOW_MS;

  return {
    isDuplicate,
    nextCache: {
      ...cache,
      lastPersistedEvent: {
        fingerprint,
        detectedAt,
      },
    },
  };
}

function buildCommonEventFields({
  playerId,
  phase,
  stageId,
  stageName,
  fieldId,
  detectedAt,
}) {
  return {
    id: `behavior-${detectedAt}-${Math.random().toString(36).slice(2, 8)}`,
    detectedAt,
    phase,
    stageId,
    stageName,
    playerId,
    fieldId: fieldId || null,
  };
}

export function recordSubmittedIntegrityText({
  playerId,
  fieldId,
  text,
  phase,
  stageId,
  stageName,
  sourceType = "submitted_text",
  detectedAt = Date.now(),
}) {
  if (!playerId) {
    return;
  }

  let cache = loadCache(playerId);
  cache = rememberAuthoredEntry(cache, {
    text,
    fieldId,
    sourceType,
    sourceAt: detectedAt,
    phase,
    stageId,
    stageName,
  });
  saveCache(playerId, cache);
}

export function useIntegrityTracking({
  enabled,
  player,
  round,
  stage,
  phase,
  defaultStageId = null,
  defaultStageName = null,
  onPreLobbyPaste,
}) {
  const cacheRef = useRef(getDefaultCache());
  const inputMetaRef = useRef({});
  // `behaviorTrackingEvents` is written through behaviorLogWriter, which keeps
  // the grow-only mirror and batches the writes; see behaviorLogWriter.js.
  // Per-field exposure, so a paste count can be read against the number of
  // free-text fields a participant actually engaged with. Without a
  // denominator, an arm with more places to paste looks dirtier than an arm
  // with fewer, whatever the participants did.
  const fieldExposureRef = useRef({});

  useEffect(() => {
    if (!enabled || !player?.id) {
      return undefined;
    }

    cacheRef.current = loadCache(player.id);

    const resolveStageId = () => stage?.id || defaultStageId || null;
    const resolveStageName = () => stage?.get?.("name") || defaultStageName || null;

    const persistBehaviorEvent = (event) => {
      const detectedAt = Number(event.detectedAt || Date.now());
      const nextEvent = {
        ...event,
        detectedAt,
      };

      const dedupeResult = shouldDropDuplicate(cacheRef.current, nextEvent);
      cacheRef.current = dedupeResult.nextCache;
      saveCache(player.id, cacheRef.current);

      if (dedupeResult.isDuplicate) {
        return;
      }

      // Paste, copy and cut feed the outside-tool filter, and a hidden page may
      // be about to close, so those are written at once; the rest wait for the
      // next batched flush.
      const writeNow =
        ["paste", "copy", "cut"].includes(String(nextEvent.type || "")) ||
        (nextEvent.type === "attention" && nextEvent.action === "document_hidden");
      behaviorLogWriter.enqueue(player, nextEvent, { immediate: writeNow });

      // The round-scoped log is a second, unmirrored read-modify-write sink and
      // it is a near-duplicate of the player-scoped one. Keep it to the two
      // event types the analysis actually cross-checks, so the new diagnostic
      // events do not double the write volume on the sink that has no mirror.
      const roundLoggableTypes = ["paste", "attention"];
      if (
        round &&
        phase !== "pre-lobby" &&
        roundLoggableTypes.includes(String(nextEvent.type || ""))
      ) {
        const existingRoundEvents = Array.isArray(round.get("eventLog"))
          ? round.get("eventLog")
          : [];
        round.set(
          "eventLog",
          sortEventLog([...existingRoundEvents, { t: detectedAt, ...nextEvent }])
        );
      }

      if (phase === "pre-lobby" && typeof onPreLobbyPaste === "function" && nextEvent.type === "paste") {
        onPreLobbyPaste(nextEvent);
      }
    };

    const rememberCurrentFieldValue = (target, sourceType, sourceAt = Date.now()) => {
      if (!isEditableElement(target)) {
        return;
      }

      cacheRef.current = rememberAuthoredEntry(cacheRef.current, {
        text: getEditableValue(target),
        fieldId: getIntegrityFieldId(target),
        sourceType,
        sourceAt,
        phase,
        stageId: resolveStageId(),
        stageName: resolveStageName(),
      });
      saveCache(player.id, cacheRef.current);
    };

    const handleBeforeInput = (event) => {
      const target = event?.target;
      if (!isEditableElement(target)) {
        return;
      }

      const fieldId = getIntegrityFieldId(target) || "unknown";
      inputMetaRef.current[fieldId] = {
        inputType: String(event.inputType || ""),
        previousValue: getEditableValue(target),
      };
    };

    const handleInput = (event) => {
      const target = event?.target;
      if (!isEditableElement(target)) {
        return;
      }

      const fieldId = getIntegrityFieldId(target) || "unknown";
      const priorMeta = inputMetaRef.current[fieldId] || {};
      const inputType = String(event.inputType || priorMeta.inputType || "");

      if (shouldCaptureAuthoredInput(inputType)) {
        rememberCurrentFieldValue(target, "typed_snapshot");
      }

      const exposure = fieldExposureRef.current[fieldId];
      if (exposure) {
        // Counts `input` events, not characters: one paste, one drop, one undo
        // and one IME composition commit each count 1, however much text they
        // move. Named for what it measures so it is not read as typing effort.
        exposure.inputEvents += 1;
      }

      inputMetaRef.current[fieldId] = {
        inputType,
        previousValue: getEditableValue(target),
      };
    };

    const handleCopyCut = (event) => {
      const text =
        getPlainTextFromClipboardEvent(event) ||
        getSelectionText(event);
      if (!text) {
        return;
      }

      const detectedAt = Date.now();
      cacheRef.current = {
        ...cacheRef.current,
        lastCopyCut: {
          text: clipText(text),
          normalized: normalizeText(text),
          sourceType: event.type === "cut" ? "cut" : "copy",
          sourceAt: detectedAt,
          fieldId: getIntegrityFieldId(event?.target),
          phase,
          stageId: resolveStageId(),
          stageName: resolveStageName(),
        },
      };

      if (event.type === "cut") {
        rememberCurrentFieldValue(event?.target, "typed_snapshot", detectedAt);
      }

      saveCache(player.id, cacheRef.current);

      // Persist the copy side too. Until now copy and cut lived only in this
      // per-tab sessionStorage cache and reached no server-side sink, so a
      // participant who copied the scenario out to an outside tool and never
      // pasted anything back left no trace at all. This half of the signal is
      // arm-symmetric by construction, because the scenario panel is identical
      // in both arms, unlike the paste side which depends on how many free-text
      // boxes the arm happens to offer.
      //
      // The copied TEXT is deliberately not persisted, only its length and
      // whether the selection carried the hidden copy-trap span. That keeps the
      // scenario text and any participant-authored text out of the event log
      // while still recording that a copy happened and what kind.
      persistBehaviorEvent({
        ...buildCommonEventFields({
          playerId: player.id,
          phase,
          stageId: resolveStageId(),
          stageName: resolveStageName(),
          fieldId: getIntegrityFieldId(event?.target),
          detectedAt,
        }),
        type: event.type === "cut" ? "cut" : "copy",
        copiedTextLength: text.length,
        copiedWordCount: normalizeText(text).split(" ").filter(Boolean).length,
        trapPayloadPresent: selectionCarriesCopyTrap(),
      });
    };

    const handlePaste = (event) => {
      const pastedText = getPlainTextFromClipboardEvent(event);
      if (!pastedText) {
        return;
      }

      const detectedAt = Date.now();
      const stageId = resolveStageId();
      const stageName = resolveStageName();
      const fieldId = getIntegrityFieldId(event?.target);
      const normalizedPaste = normalizeText(pastedText);

      let provenance = "no_internal_provenance";
      let matchedSourceType = null;
      let matchedSourceAt = null;

      const lastCopyCut = cacheRef.current.lastCopyCut;
      if (
        lastCopyCut?.normalized &&
        lastCopyCut.normalized === normalizedPaste
      ) {
        provenance = "internal_copy_cut_match";
        matchedSourceType = lastCopyCut.sourceType || "copy";
        matchedSourceAt = lastCopyCut.sourceAt || null;
      } else {
        const priorAuthoredMatch = findPriorAuthoredMatch(cacheRef.current, pastedText);
        if (priorAuthoredMatch) {
          provenance = "prior_typed_match";
          matchedSourceType = priorAuthoredMatch.sourceType || "typed_snapshot";
          matchedSourceAt = priorAuthoredMatch.sourceAt || null;
        }
      }

      persistBehaviorEvent({
        ...buildCommonEventFields({
          playerId: player.id,
          phase,
          stageId,
          stageName,
          fieldId,
          detectedAt,
        }),
        type: "paste",
        pastedText,
        pastedTextLength: pastedText.length,
        provenance,
        matchedSourceType,
        matchedSourceAt,
      });
    };

    const recordAttentionEvent = (action) => {
      const detectedAt = Date.now();
      persistBehaviorEvent({
        ...buildCommonEventFields({
          playerId: player.id,
          phase,
          stageId: resolveStageId(),
          stageName: resolveStageName(),
          fieldId: null,
          detectedAt,
        }),
        type: "attention",
        action,
        visibilityState: typeof document !== "undefined" ? document.visibilityState : null,
        hasFocus: typeof document !== "undefined" ? document.hasFocus() : null,
      });
    };

    // Exposure to a free-text field: when it was entered, how long the
    // participant stayed in it, and how much they typed. This is the
    // paste-opportunity denominator. The Socratic arm offers one chat box and
    // the Template arm a fixed form, so a raw paste count is not comparable
    // across arms; a paste count per engaged field is.
    const handleFocusIn = (event) => {
      const target = event?.target;
      if (!isEditableElement(target)) {
        return;
      }
      const fieldId = getIntegrityFieldId(target) || "unknown";
      fieldExposureRef.current[fieldId] = {
        enteredAt: Date.now(),
        inputEvents: 0,
        hiddenMs: 0,
        hiddenSince: null,
      };
    };

    // Time the window spends blurred or hidden is subtracted from dwell. Without
    // this, alt-tabbing to an outside tool while the cursor sits in a textarea
    // INFLATES the field's dwell, so the denominator grows with exactly the
    // behaviour it is meant to normalise against and the heaviest outside-tool
    // user gets the lowest paste-per-second.
    const markExposuresHidden = () => {
      const now = Date.now();
      Object.values(fieldExposureRef.current).forEach((exposure) => {
        if (exposure && exposure.hiddenSince === null) {
          exposure.hiddenSince = now;
        }
      });
    };

    const markExposuresVisible = () => {
      const now = Date.now();
      Object.values(fieldExposureRef.current).forEach((exposure) => {
        if (exposure && exposure.hiddenSince !== null) {
          exposure.hiddenMs += Math.max(0, now - exposure.hiddenSince);
          exposure.hiddenSince = null;
        }
      });
    };

    const emitFieldExposure = (fieldId, exposure) => {
      if (!exposure) {
        return;
      }
      const detectedAt = Date.now();
      const dwellMs = Math.max(0, detectedAt - exposure.enteredAt);
      const hiddenMs =
        exposure.hiddenMs +
        (exposure.hiddenSince === null
          ? 0
          : Math.max(0, detectedAt - exposure.hiddenSince));
      // Drop the tab-through case, which is exposure to nothing.
      if (dwellMs < 250 && exposure.inputEvents === 0) {
        return;
      }

      persistBehaviorEvent({
        ...buildCommonEventFields({
          playerId: player.id,
          phase,
          stageId: resolveStageId(),
          stageName: resolveStageName(),
          fieldId,
          detectedAt,
        }),
        type: "field_exposure",
        dwellMs,
        activeDwellMs: Math.max(0, dwellMs - hiddenMs),
        hiddenMs,
        inputEvents: exposure.inputEvents,
      });
    };

    // Emit every exposure still open and clear the map. Needed because a stage
    // advance unmounts the focused element without firing focusout, so the field
    // the participant was sitting in when they submitted, usually the one they
    // engaged with most, would otherwise never be recorded.
    const flushOpenExposures = () => {
      const pending = fieldExposureRef.current;
      fieldExposureRef.current = {};
      Object.keys(pending).forEach((fieldId) => {
        emitFieldExposure(fieldId, pending[fieldId]);
      });
    };

    const handleFocusOut = (event) => {
      const target = event?.target;
      if (!isEditableElement(target)) {
        return;
      }
      const fieldId = getIntegrityFieldId(target) || "unknown";
      const exposure = fieldExposureRef.current[fieldId];
      if (!exposure) {
        return;
      }
      delete fieldExposureRef.current[fieldId];
      emitFieldExposure(fieldId, exposure);
    };

    const handlePageHide = () => {
      flushOpenExposures();
      behaviorLogWriter.flush(player);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        markExposuresHidden();
      } else {
        markExposuresVisible();
      }
      recordAttentionEvent(document.hidden ? "document_hidden" : "document_visible");
    };

    const handleWindowBlur = () => {
      markExposuresHidden();
      recordAttentionEvent("window_blur");
    };

    const handleWindowFocus = () => {
      markExposuresVisible();
      recordAttentionEvent("window_focus");
    };

    document.addEventListener("beforeinput", handleBeforeInput, true);
    document.addEventListener("input", handleInput, true);
    document.addEventListener("copy", handleCopyCut, true);
    document.addEventListener("cut", handleCopyCut, true);
    document.addEventListener("paste", handlePaste, true);
    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("focusout", handleFocusOut, true);
    window.addEventListener("pagehide", handlePageHide, true);
    document.addEventListener("visibilitychange", handleVisibilityChange, true);
    window.addEventListener("blur", handleWindowBlur, true);
    window.addEventListener("focus", handleWindowFocus, true);

    return () => {
      document.removeEventListener("beforeinput", handleBeforeInput, true);
      document.removeEventListener("input", handleInput, true);
      document.removeEventListener("copy", handleCopyCut, true);
      document.removeEventListener("cut", handleCopyCut, true);
      document.removeEventListener("paste", handlePaste, true);
      flushOpenExposures();
      behaviorLogWriter.flush(player);
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleFocusOut, true);
      window.removeEventListener("pagehide", handlePageHide, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange, true);
      window.removeEventListener("blur", handleWindowBlur, true);
      window.removeEventListener("focus", handleWindowFocus, true);
    };
  }, [
    defaultStageId,
    defaultStageName,
    enabled,
    onPreLobbyPaste,
    phase,
    player,
    round,
    stage,
  ]);
}
